<template>
    <a-card :bordered="false" class="bf-card">
        <a-table
            size="small"
            :columns="columns"
            :data-source="rows"
            :loading="is_loading"
            row-key="key"
            :expandedRowKeys="expandedKeys"
            :locale="{ emptyText: 'Nenhum pré-pedido pendente.' }"
            @expand="onExpand"
        >
            <template slot="fornecedor" slot-scope="text, record">
                <div class="bf-sup">
                    <span class="bf-n">{{ record.provider_name }}</span>
                    <span class="bf-v" v-if="record.seller_name">{{ record.seller_name }}</span>
                </div>
            </template>
            <template slot="lojas" slot-scope="text, record">{{ record.stores.length }}</template>
            <template slot="valor" slot-scope="text, record">{{ record.total | customCurrency }}</template>
            <template slot="acoes" slot-scope="text, record">
                <a-button
                    class="bf-btn"
                    :loading="!!sending[record.key]"
                    :disabled="!!sending[record.key]"
                    @click="sendOrders(record)"
                >
                    Enviar pedidos
                </a-button>
            </template>

            <template slot="expandedRowRender" slot-scope="record">
                <a-table
                    class="bf-inner-table"
                    size="small"
                    :columns="productColumns"
                    :data-source="productsCache[record.key] || []"
                    :loading="!!loadingProducts[record.key]"
                    :pagination="false"
                    row-key="id"
                >
                    <template slot="produto" slot-scope="text, prod">
                        <div class="bf-sup">
                            <span class="bf-n">{{ prod.name }}</span>
                            <span class="bf-v" v-if="prod.ean || prod.internal_code">
                                <template v-if="prod.ean">EAN {{ prod.ean }}</template>
                                <template v-if="prod.ean && prod.internal_code"> · </template>
                                <template v-if="prod.internal_code">Cód. {{ prod.internal_code }}</template>
                            </span>
                        </div>
                    </template>
                    <template slot="ressuprimento" slot-scope="text, prod">
                        <span v-if="replenishment(prod)" class="bf-repl">{{ replenishment(prod) }}</span>
                        <span v-else>--</span>
                    </template>
                    <template slot="quantidade" slot-scope="text, prod">{{ prod.requested_quantity }}</template>
                    <template slot="embalagem" slot-scope="text, prod">{{ prod.packing }}</template>
                    <template slot="preco" slot-scope="text, prod">{{ prod.offer_price | customCurrency }}</template>
                    <template slot="ultimaCompra" slot-scope="text, prod">
                        <span v-if="prod.last_purchase != null">{{ prod.last_purchase | customCurrency }}</span>
                        <span v-else>--</span>
                    </template>
                    <template slot="diferenca" slot-scope="text, prod">
                        <span v-if="prod.last_purchase != null" :class="deltaClass(prod)">
                            {{ (prod.offer_price - prod.last_purchase) | customCurrency }}
                        </span>
                        <span v-else>--</span>
                    </template>

                    <template slot="variacao" slot-scope="text, prod">
                        <span v-if="prod.last_purchase != null" :class="deltaClass(prod)">
                            {{ variation(prod).toFixed(1) }}%
                        </span>
                        <span v-else>--</span>
                    </template>
                    <template slot="acoes" slot-scope="record">
                        <a-tooltip title="Análise de giro">
                            <a-button type="link" size="small" @click="$emit('giro', record)">
                                <span class="material-symbols-outlined">query_stats</span>
                            </a-button>
                        </a-tooltip>
                    </template>
                </a-table>
            </template>
        </a-table>
    </a-card>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import RequestService from "@/services/v3/request.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import currencyPrecision from "@/mixins/currencyPrecision";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import { replenishmentLabel } from "@/helpers/replenishment";

export default {
    name: "ByFornecedorTable",
    mixins: [currencyPrecision, loaderMixin],
    props: {
        // Flat rows from GET clients/requests/{id}/pre-orders: one per
        // (loja, vendedor, fornecedor). Already filtered to pending only.
        pre_orders: { type: Array, default: () => [] },
        is_loading: { type: Boolean, default: false },
        pendencyOnly: { type: Boolean, default: false },
    },
    data() {
        return {
            svc: new QuotationService(),
            v3Svc: new RequestService(),
            sending: {},
            expandedKeys: [],
            loadingProducts: {},
            productsCache: {},
        };
    },
    computed: {
        openProductQuickView(record){
            this.$emit('giro', record)
        },
        columns() {
            return [
                { title: "Fornecedor", key: "fornecedor", scopedSlots: { customRender: "fornecedor" } },
                { title: "Nº de lojas", key: "lojas", align: "right", scopedSlots: { customRender: "lojas" } },
                { title: "Valor total", key: "valor", align: "right", scopedSlots: { customRender: "valor" } },
                { title: "Ações", key: "acoes", scopedSlots: { customRender: "acoes" } },
            ];
        },
        productColumns() {
            return [
                { title: "Produto", key: "produto", scopedSlots: { customRender: "produto" } },
                { title: "Reabastecimento", key: "ressuprimento", width: 130, scopedSlots: { customRender: "ressuprimento" } },
                { title: "Quantidade", key: "quantidade", align: "right", scopedSlots: { customRender: "quantidade" } },
                { title: "Embalagem", key: "embalagem", scopedSlots: { customRender: "embalagem" } },
                { title: "Preço", key: "preco", align: "right", scopedSlots: { customRender: "preco" } },
                { title: "Última Compra", key: "ultimaCompra", align: "right", scopedSlots: { customRender: "ultimaCompra" } },
                { title: "Diferença", key: "diferenca", width: 100, align: "right", scopedSlots: { customRender: "diferenca" } },
                { title: "Variação", key: "variacao", width: 90, align: "right", scopedSlots: { customRender: "variacao" } },
                { title: "Ações", key: "acoes", width: 70, align: "center", scopedSlots: { customRender: "acoes" }, },
            ];
        },
        rows() {
            // Group the flat rows back up by fornecedor+vendedor, which is the
            // grain this view shows. The endpoint returns one row per store, so
            // a provider selling into three lojas arrives as three rows.
            //
            // No possuiPedido filter any more: already-ordered rows never reach
            // the client, they are excluded server-side by cpv.status.
            const map = {};

            (this.pre_orders || []).forEach((po) => {
                const key = po.provider.id + "-" + po.seller.id;

                if (!map[key]) {
                    map[key] = {
                        key,
                        provider_id: po.provider.id,
                        seller_id: po.seller.id,
                        provider_name: po.provider.name,
                        seller_name: po.seller.name,
                        total: 0,
                        stores: [],
                    };
                }

                map[key].total += po.total || 0;
                map[key].stores.push({
                    store_id: po.store.id,
                    store_name: po.store.name,
                    total: po.total,
                    below_minimum: po.below_minimum,
                });
            });

            const list = Object.values(map);

            // below_minimum is computed server-side (and guards minimum = 0),
            // so there is no faturamento_minimo comparison to repeat here.
            return this.pendencyOnly
                ? list.filter((r) => r.stores.some((s) => s.below_minimum))
                : list;
        },
    },
    methods: {
        replenishment(product) {
            return replenishmentLabel(product.replenishment_strategy);
        },
        deltaClass(prod) {
            if (prod.offer_price > prod.last_purchase) return "bf-neg";
            if (prod.offer_price < prod.last_purchase) return "bf-pos";
            return "";
        },
        variation(prod) {
            if (!prod.last_purchase) return 0;
            return ((prod.offer_price - prod.last_purchase) / prod.last_purchase) * 100;
        },
        onExpand(isExpanded, record) {
            const idx = this.expandedKeys.indexOf(record.key);
            if (isExpanded) {
                if (idx === -1) this.expandedKeys.push(record.key);
                this.loadProducts(record);
            } else if (idx !== -1) {
                this.expandedKeys.splice(idx, 1);
            }
        },
        loadProducts(record) {
            if (this.productsCache[record.key]) return;
            this.$set(this.loadingProducts, record.key, true);
            this.v3Svc
                .request_offers(this.$route.params.id, record.seller_id)
                .then((response) => response.data)
                .then((data) => {
                    data.forEach((p) => {
                        const parts = (p.packing || "").split(" ");
                        p.embalagem_units = parts[0];
                        p.embalagem_quantity = parts[1];
                        p.seller_id = p.seller_id || record.idVendedor;
                        p.provider_id = p.provider_id || record.idFornecedor;
                    });
                    this.$set(this.productsCache, record.key, data);
                    this.$set(this.loadingProducts, record.key, false);
                })
                .catch((error) => {
                    this.$set(this.loadingProducts, record.key, false);
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        sendOrders(record) {
            this.confirm_action({
                message: `Enviar pedido(s) para ${record.provider_name} em ${record.stores.length} loja(s)?`,
                confirm_text: "Enviar pedidos",
                callback: () => {
                    this.$set(this.sending, record.key, true);
                    Promise.all(
                        record.stores.map((s) =>
                            this.svc.order({ idcotacao: this.$route.params.id, loja: s.store_id, idvendedor: record.seller_id })
                        )
                    )
                        .then(() => {
                            this.snack("Pedidos enviados com sucesso.");
                            this.$set(this.sending, record.key, false);
                            this.$emit("reload");
                        })
                        .catch(() => {
                            this.$set(this.sending, record.key, false);
                            this.snack("Não foi possível enviar um ou mais pedidos.", "error");
                        });
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.bf-card {
    --pri: var(--primary-color);
    width: 100%;

    ::v-deep(.ant-card-body) {
        width: 100%;
        padding: 0;
    }
    ::v-deep(.ant-table-wrapper),
    ::v-deep(.ant-table),
    ::v-deep(.ant-table-content),
    ::v-deep(table) {
        width: 100%;
    }
}
.bf-inner-table {
    background: #fff;
}
.bf-sup {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.bf-n {
    font-weight: 600;
    color: #262626;
}
.bf-v {
    font-size: 12px;
    color: #8c8c8c;
    font-style: italic;
}
.bf-repl {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    background: #f0f0f0;
    color: #595959;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.bf-pos {
    color: #389e0d;
    font-weight: 600;
}
.bf-neg {
    color: #cf1322;
    font-weight: 600;
}
.bf-btn.ant-btn,
.bf-btn.ant-btn:hover,
.bf-btn.ant-btn:focus,
.bf-btn.ant-btn:active {
    height: 30px;
    padding: 0 14px;
    border: 1px solid var(--pri);
    background: var(--primary-color-opacity, rgba(232, 96, 44, 0.08));
    color: var(--pri);
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    box-shadow: none;
}
.bf-btn.ant-btn[disabled],
.bf-btn.ant-btn[disabled]:hover {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
